import React from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Users, Target, Lightbulb, Building, Award, ExternalLink, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BirimDanismaKurulu = () => {
  const { t } = useTranslation();

  const scrollToContent = () => {
    document.getElementById('kurulu-content').scrollIntoView({
      behavior: 'smooth'
    });
  };

  const members = [
    {
      id: 1,
      name: 'Prof. Dr. Hatice MERGEN',
      position: t('advisory_board.dean'),
      role: t('advisory_board.board_president'),
      institution: 'Hacettepe Üniversitesi Fen Fakültesi',
      photo: 'https://avesis.hacettepe.edu.tr/user/image/5582',
      type: 'academic'
    },
    {
      id: 2,
      name: 'Prof. Dr. Canan KÖROĞLU',
      position: t('advisory_board.vice_dean'),
      role: t('advisory_board.board_member'),
      institution: 'Hacettepe Üniversitesi Fen Fakültesi',
      photo: 'https://avesis.hacettepe.edu.tr/user/image/6842',
      type: 'academic'
    },
    {
      id: 3,
      name: 'Prof. Dr. Barış TEMELLİ',
      position: t('advisory_board.vice_dean'),
      role: t('advisory_board.board_member'),
      institution: 'Hacettepe Üniversitesi Fen Fakültesi',
      photo: 'https://avesis.hacettepe.edu.tr/user/image/5902',
      type: 'academic'
    },
    {
      id: 4,
      name: 'İlkay BAYINDIR SÖKMEN',
      position: t('advisory_board.glass_technologies_director'),
      role: t('advisory_board.external_stakeholder'),
      institution: 'Şişecam Bilim Teknolojileri ve Tasarım Merkezi',
      photo: 'https://fs.hacettepe.edu.tr/fenyeni/resimler/ilkaysokmen.jpg',
      type: 'external'
    },
    {
      id: 5,
      name: 'Perihan ÇÖÇELLİ',
      position: t('advisory_board.chairman_of_the_board'),
      role: t('advisory_board.external_stakeholder'),
      institution: 'Persu Suni Deri San. ve Tic. LTD. ŞTİ.',
      photo: 'https://fs.hacettepe.edu.tr/fenyeni/resimler/perihan-cocelli.jpg',
      type: 'external'
    },
    {
      id: 6,
      name: 'Şirzat ÇETİNKAYA',
      position: t('advisory_board.president_of_actuarial_association'),
      role: t('advisory_board.external_stakeholder'),
      institution: 'Sompo Sigorta',
      photo: 'https://fs.hacettepe.edu.tr/fenyeni/resimler/Sompo_Sigorta_0396_TSEV.jpg',
      type: 'external'
    },
    {
      id: 7,
      name: 'Tolga KASKATI',
      position: t('advisory_board.group_manager'),
      role: t('advisory_board.external_stakeholder'),
      institution: 'IBYS Grup',
      photo: 'https://fs.hacettepe.edu.tr/fenyeni/resimler/tolgakaskatı.jpg',
      type: 'external'
    },
    {
      id: 8,
      name: 'Tayfun OLTULU',
      position: t('advisory_board.chemists_association_president'),
      role: t('advisory_board.external_stakeholder'),
      institution: 'Kimyagerler Derneği İç Anadolu Yönetim Kurulu',
      photo: 'https://fs.hacettepe.edu.tr/fenyeni/e-dosyalar/tayfunoltulu.jpg',
      type: 'external'
    }
  ];

  const objectives = [
    {
      icon: Building,
      title: t('advisory_board.objectives.industry_collaboration.title'),
      description: t('advisory_board.objectives.industry_collaboration.description'),
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Target,
      title: t('advisory_board.objectives.strategic_planning.title'),
      description: t('advisory_board.objectives.strategic_planning.description'),
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Lightbulb,
      title: t('advisory_board.objectives.innovation_development.title'),
      description: t('advisory_board.objectives.innovation_development.description'),
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      icon: Award,
      title: t('advisory_board.objectives.sustainable_impact.title'),
      description: t('advisory_board.objectives.sustainable_impact.description'),
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'academic':
        return 'from-blue-500/10 to-blue-600/10 border-blue-200';
      case 'student':
        return 'from-green-500/10 to-green-600/10 border-green-200';
      case 'external':
        return 'from-purple-500/10 to-purple-600/10 border-purple-200';
      default:
        return 'from-gray-500/10 to-gray-600/10 border-gray-200';
    }
  };

  

  return (
    <div className="font-sans">
      {/* Full-screen Video Hero */}
      <div className="h-screen w-full relative overflow-hidden">
        <video
          className="absolute top-1/2 left-1/2 w-full h-full object-cover transform -translate-x-1/2 -translate-y-1/2"
          src="/fenfakultesivideo.mp4"
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center">
          <motion.h1 
            className="text-5xl md:text-8xl font-black text-white uppercase" 
            style={{ textShadow: '0px 5px 20px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            {t('advisory_board.title')}
          </motion.h1>
          <motion.p 
            className="mt-4 text-lg md:text-xl text-white/90 font-medium max-w-4xl px-4" 
            style={{ textShadow: '0px 2px 10px rgba(0,0,0,0.5)' }}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          >
            {t('advisory_board.subtitle')}
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2 cursor-pointer z-20"
          onClick={scrollToContent}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="w-10 h-10 text-white/80 hover:text-white transition-colors"/>
        </motion.div>
      </div>

      {/* Page Content */}
      <div id="kurulu-content" className="relative bg-gray-50 py-20">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Description Section */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-xl border border-white/20">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
                {t('advisory_board.about_board')}
              </h2>
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                <p className="text-xl mb-6">
                  {t('advisory_board.p1')}
                </p>
                <p className="text-lg mb-6">
                  {t('advisory_board.p2')}
                </p>
                <p className="text-lg">
                  {t('advisory_board.p3')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Objectives Section */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
              {t('advisory_board.our_objectives')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {objectives.map((objective, index) => (
                <motion.div
                  key={index}
                  className="group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-white/70 backdrop-blur-xl rounded-2xl p-6 h-full shadow-lg border border-white/20 hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${objective.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <objective.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{objective.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{objective.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Members Section */}
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              {t('advisory_board.board_members')}
            </h2>
            <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
              {t('advisory_board.board_members_subtitle')}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {members.map((member, index) => (
                <motion.div
                  key={member.id}
                  className="group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`bg-gradient-to-br ${getTypeColor(member.type)} backdrop-blur-xl rounded-3xl p-6 h-full shadow-lg border hover:shadow-xl transition-all duration-300 group-hover:-translate-y-2`}>
                    {/* Photo Placeholder */}
                    <div className="relative mb-6">
                      <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center overflow-hidden shadow-lg">
                        {member.photo ? (
                          <img 
                            src={member.photo} 
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-3xl">👤</span>
                        )}
                      </div>
                      
                    </div>

                    <div className="text-center">
                      <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                        {member.name}
                      </h3>
                      <p className="text-sm font-semibold text-blue-600 mb-2">
                        {member.role}
                      </p>
                      <p className="text-sm text-gray-600 mb-3">
                        {member.position}
                      </p>
                      <div className="bg-white/50 rounded-lg p-3 mt-4">
                        <p className="text-xs text-gray-700 font-medium">
                          {member.institution}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Legal Document Section */}
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-white/20">
              <div className="flex items-center justify-center mb-6">
                <FileText className="w-12 h-12 text-blue-600 mr-4" />
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {t('advisory_board.legal_basis')}
                </h2>
              </div>
              <p className="text-lg text-gray-700 mb-6">
                {t('advisory_board.legal_basis_description')}
              </p>
              <motion.a
                href="https://hacettepe.edu.tr/fs/yonerge_kalite.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold py-3 px-8 rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FileText className="w-5 h-5 mr-2" />
                {t('advisory_board.view_directive')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BirimDanismaKurulu; 